import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { AlertService } from '../../commons/services/alert.service';
import { AppState } from '../reducers';
import { getSelectedStoreTenant } from '../selectors/tenant.selectors';
import * as RidersActions from '../actions/riders.actions';
import { getSelectionDialogId, getRiderDialogId, getRidersTableState } from '../selectors/riders.selectors';
import { LaravelRidersService } from '../../commons/services/laravel/laravel-riders.service';
import { NewRiderComponent } from '../../modules/home/modules/riders/components/new-rider/new-rider.component';
import { DialogComponent } from '../../modules/shared/components/dialog/dialog.component';

@Injectable()
export class RiderEffects{
    error$ = createEffect(
        () =>
          this.actions$.pipe(
            ofType(
                RidersActions.deleteRiderFailed,
                RidersActions.saveRiderFailed
            ),
            tap(({ error }) => {
              if (error) {
                this.alertService.showErrorMessage('Errore', error);
              }
            })
          ),
        { dispatch: false }
      );

    loadRiders$ = createEffect(() =>
      this.actions$.pipe(
          ofType(RidersActions.loadRiders),
          withLatestFrom(this.store$.select(getSelectedStoreTenant)),
          map(([{ page, perPage, order, direction, filters, includes }, tenant]) => {
            const filter= {...filters};
            if(filter.onlyCurrentTenant){
              filter.defaultTenantId = tenant.id
            }
            return {page, perPage, order, direction, filters: filter, includes};
          }),
          switchMap(({page, perPage, order, direction, filters, includes}) => {
              return this.riderService
              .list(page, perPage, order, direction, filters, includes)
              .pipe(
                  map((result) => {
                    return RidersActions.loadRidersCompleted({
                          riders: result.data,
                          currentPage: page,
                          total: result.total,
                          perPage,
                          order,
                          direction,
                          filters,
                          includes
                      });
                  }),
                  catchError((error) =>
                      of(RidersActions.loadRidersFailed({ error })))
              );
          })
        )
    );
 
    changePage = createEffect(() =>
        this.actions$.pipe(
          ofType(RidersActions.changePage),
          withLatestFrom(this.store$.select(getRidersTableState)),
          map(
            ([
              { page, size },
              { total, currentPage, perPage, direction, order, filters, includes },
            ]) =>
                RidersActions.loadRiders({
                page: page,
                perPage: size,
                order,
                direction,
                filters,
                includes,
              })
          )
        )
    );

    changeSort = createEffect(() =>
        this.actions$.pipe(
          ofType(RidersActions.changeSort),
          withLatestFrom(this.store$.select(getRidersTableState)),
          map(
            ([
              action,
              { total, currentPage, perPage, direction, order, filters, includes },
            ]) =>
              RidersActions.loadRiders({
                page: currentPage,
                perPage: perPage,
                order: action.order,
                direction: action.direction,
                filters,
                includes,
              })
          )
        )
    );
    
    changeFilters = createEffect(() =>
        this.actions$.pipe(
          ofType(RidersActions.changeFilters),
          withLatestFrom(this.store$.select(getRidersTableState)),
          map(
            ([
              { filters },
              { total, currentPage, perPage, direction, order, includes },
            ]) =>
                RidersActions.loadRiders({
                page: currentPage,
                perPage: perPage,
                order,
                direction,
                filters,
                includes,
              })
          )
        )
    );

    editRider$ = createEffect(() =>
      this.actions$.pipe(
        ofType(RidersActions.editRider),
        map(({ rider }) => {
          let dialogRef = this.dialog.open(NewRiderComponent, {
            data: {
              rider,
            },
            panelClass: 'full-screen-dialog',
          });
          return RidersActions.riderDialogOpened({
            dialogId: dialogRef.id,
          });
        })
      )
    );

    saveRider$ = createEffect(() =>
      this.actions$.pipe(
        ofType(RidersActions.saveRider),
        withLatestFrom(this.store$.select(getSelectedStoreTenant)),
        mergeMap(([{ riders }, selectedTenant]) => {
          let tenantId = selectedTenant ? selectedTenant.id : null;
            let riderSave = {
            ...riders.toDTO(),
            default_tenant_id: tenantId
          };
  
          return this.riderService.upsert(riderSave).pipe(
            map((result) =>
              RidersActions.saveRiderCompleted({ rider: result })
            ),
            // catchError((error) =>
            //   of(RidersActions.saveRiderFailed({ error }))
            // ),
            tap((action) => {
              if(action.type === RidersActions.saveRiderCompleted.type){
                this.alertService.showConfirmMessage(`Fattorino con id: ${action.rider.id} salvato con successo`)
                this.store$.dispatch(RidersActions.closeRidersDialog());
              } else {
                this.store$.dispatch(RidersActions.saveRiderFailed({ error: Error }));
              }
            })
          );
        })
      )
    );

    // onSaveCompleted$ = createEffect(() =>
    //     this.actions$.pipe(
    //       ofType(RidersActions.saveRiderCompleted),
    //       map((action) => action.rider),
    //       tap((rider) =>
    //         this.alertService.showConfirmMessage(
    //           `Fattorino con id: ${rider.id} salvato con successo`
    //         )
    //       ),
    //       map(() => RidersActions.closeRidersDialog())
    //     )
    // );

    deleteRider$ = createEffect(() =>
        this.actions$.pipe(
          ofType(RidersActions.deleteRider),
          switchMap(({ rider }) =>
            this.alertService
              .showConfirmDialog(
                'Conferma eliminazione',
                `Sei sicuro di voler eliminare il fattorino?`
              )
              .pipe(
                mergeMap((confirm) => {
                  return confirm
                    ? this.riderService.delete(rider.id).pipe(
                        map(() =>
                            RidersActions.deleteRiderCompleted({
                            rider,
                          })
                        ),
                        catchError((error) =>
                          of(RidersActions.deleteRiderFailed({ error }))
                        ),
                        tap((action) => {
                          if(action.type === RidersActions.deleteRiderCompleted.type){
                            this.alertService.showConfirmMessage(`Fattorino con id: ${rider.id} rimosso con successo`)
                            this.store$.dispatch(RidersActions.closeRidersDialog());
                          }
                        })
                      )
                    : of(RidersActions.deleteRiderCancelled());
                })
              )
          )
        )
    );

    // onDeleteCompleted$ = createEffect(() =>
    //     this.actions$.pipe(
    //       ofType(RidersActions.deleteRiderCompleted),
    //       tap(({ rider }) =>
    //         this.alertService.showConfirmMessage(
    //           `Fattorino con id: ${rider.id} rimosso con successo`
    //         )
    //       ),
    //       map(() => RidersActions.closeRidersDialog())
    //     )
    // );

    // closeDialog = createEffect(
    //     () =>
    //       this.actions$.pipe(
    //         ofType(RidersActions.closeRidersDialog),
    //         withLatestFrom(this.store$.select(getRiderDialogId)),
    //         tap(([_, dialogId]) => {
    //           if (dialogId) {
    //             this.dialog.getDialogById(dialogId).close();
    //           }
    //         })
    //       ),
    //     { dispatch: false }
    // );

    reloadAfterSave = createEffect(() =>
        this.actions$.pipe(
          ofType(RidersActions.saveRiderCompleted),
          withLatestFrom(this.store$.select(getRidersTableState)),
          map(
            ([_, { currentPage, perPage, direction, order, filters, includes }]) =>
              RidersActions.loadRiders({
                page: currentPage,
                perPage,
                order,
                direction,
                filters,
                includes,
              })
          )
        )
    );

    reloadAfterDelete = createEffect(() =>
        this.actions$.pipe(
          ofType(RidersActions.deleteRiderCompleted),
          withLatestFrom(this.store$.select(getRidersTableState)),
          map(
            ([_, { currentPage, perPage, direction, order, filters, includes }]) =>
              RidersActions.loadRiders({
                page: currentPage,
                perPage,
                order,
                direction,
                filters,
                includes,
              })
          )
        )
    );

    closeSelectionDialog = createEffect(
        () =>
          this.actions$.pipe(
            ofType(RidersActions.closeSelectionDialog),
            withLatestFrom(this.store$.select(getSelectionDialogId)),
            tap(([_, dialogId]) => {
              if (dialogId) {
                this.dialog.getDialogById(dialogId).close();
              }
            })
          ),
        { dispatch: false }
    );


    requestCloseDialog$ = createEffect(() =>
        this.actions$.pipe(
          ofType(RidersActions.requestCloseRidersDialog),
          switchMap(() => {
            const confirmDialogRef = this.dialog.open(DialogComponent, {
              disableClose: false
            });
      
            // Imposta il messaggio del dialog
            confirmDialogRef.componentInstance.title = "Chiudi";
            confirmDialogRef.componentInstance.confirmMessage =
              "Ci sono modifiche non salvate. Sei sicuro di voler chiudere?";
      
            return confirmDialogRef.afterClosed().pipe(
              map(result =>
                result
                  ? RidersActions.confirmCloseRidersDialog()
                  : RidersActions.cancelCloseRidersDialog()
              )
            );
          })
        )
      );
      
      
      confirmCloseDialog$ = createEffect(() =>
        this.actions$.pipe(
          ofType(RidersActions.confirmCloseRidersDialog),
          map(() => RidersActions.closeRidersDialog())
        )
      );
      
      cancelCloseDialog$ = createEffect(() =>
        this.actions$.pipe(
          ofType(RidersActions.cancelCloseRidersDialog),
          map(() => {
            // Se l'utente annulla mantiene il dialog aperto
            return { type: 'NO_ACTION' };
          })
        )
      );
      
      closeDialog$ = createEffect(
        () =>
          this.actions$.pipe(
            ofType(RidersActions.closeRidersDialog),
            withLatestFrom(this.store$.select(getRiderDialogId)),
            tap(([_, dialogId]) => {
              if (dialogId) {
                this.dialog.getDialogById(dialogId).close();
              }
            })
          ),
        { dispatch: false }
      );
        
        

      constructor(
        private actions$: Actions,
        private store$: Store<AppState>,
        private dialog: MatDialog,
        private alertService: AlertService,
        private riderService: LaravelRidersService
      ) {}
}